import React, { Fragment, useState } from 'react';
import { Button, CheckBox, DataTable, DropDown, Icon, Stack, Tab, Tabs, Text, TextField, Tree, TreeNode, TreeItem, TokenField, Property } from '../html';
import { CHECK, Content, TYPES } from './ContentType';
import './ContentEditor.css';
import { FORMATS } from './ContentType';
import { ColorField } from '../html/color';

/**
 * Content Editor
 */
export const ContentEditor = ({ content, filter, outlined = true, className, onChange }) => {

  function change(id, value) {
    const nextValue = Object.assign({}, content.value(), { [id]: value })
    if (onChange) onChange(nextValue)
  }

  const sections = content.sections()

  return (
    <div className={`content-editor ${className}`}>
      {sections.map((section) => {

        const { title, fields } = section
        const filtered = fields
          .filter(field => field.id !== 'id')
          .filter(field => filter ? filter(field, content) : true)

        return filtered.length > 0 ? (
          <section key={title}>
            {title && title.length > 0 ? <header>{title}</header> : null}
            <main>
              {filtered.map((field) => <FieldEditor key={field.id} field={field} onChange={change} outlined={outlined} content={content} />)}
            </main>
          </section>
        ) : null

      })}
    </div>
  )
}

/**
 * Tabbed Content Editor
 */
export const TabbedContentEditor = ({ content, filter, grouped = false, onChange }) => {

  const [tab, setTab] = useState(0)

  function change(id, value) {
    const nextValue = Object.assign({}, content.value(), { [id]: value })
    if (onChange) onChange(nextValue)
  }

  function changeTab(tab) {
    setTab(tab)
  }

  function group(section) {
    const { fields = [] } = section
    const groups = fields.reduce((groups, field) => {
      const groupName = field.group || ""
      const group = groups[groupName] || { name: groupName, fields: [] }
      group.fields.push(field)
      groups[groupName] = group
      return groups
    }, {})
    return Object.values(groups)
  }

  const sections = content.sections()

  return (
    <div className='content-editor tabbed'>
      <Tabs selected={tab} onChange={changeTab}>
        {sections.map(section => <Tab label={section.title} />)}
      </Tabs>
      <Stack selected={tab}>
        {sections.map(section => {
          const { title, fields } = section
          return (
            <section key={title}>
              <nav>
                &nbsp;
              </nav>
              <main>
                {
                  grouped ?
                    group(section)
                      .map(group => {
                        return (
                          <Fragment>
                            {group.name.length > 0 ? <div className="group-caption">{group.name}</div> : null}
                            {
                              group.fields
                                .filter(field => field.id !== 'id')
                                .filter(field => filter ? filter(field) : true)
                                .map((field) => <FieldEditor key={field.id} field={field} onChange={change} content={content} />)
                            }
                          </Fragment>
                        )
                      })
                    :
                    fields
                      .filter(field => field.id !== 'id')
                      .filter(field => filter ? filter(field) : true)
                      .map((field) => <FieldEditor key={field.id} field={field} onChange={change} content={content} />)
                }
              </main>
            </section>
          )
        })}
      </Stack>
    </div>
  )
}

/**
 * TreededContentEditor 
 */
export const TreededContentEditor = ({ content, filter, onChange }) => {

  const value = content.value()
  const nodes = Object.values(content.type).filter(field => field.type === TYPES.ARRAY)
  const [selected, setSelected] = useState()

  function select(index, field, node) {
    const item = new Content(node.item, field)
    setSelected({ index, item, node, value: value[node.id] })
  }

  function change(form) {
    const nextNode = selected.value.slice()
    nextNode[selected.index] = form
    const nextContent = Object.assign({}, value, { [selected.node.id]: nextNode })
    if (onChange) onChange(nextContent)
  }

  function add() {
    //TODO
  }

  return (
    <div className="content-editor treeded-editor">
      <menu>
        <header>
          <Text use='caption'>Tree Editor</Text>
        </header>
        <Tree>
          {nodes.map((node, index) => (
            <TreeNode icon="folder" label={node.label} actions={[<Icon size="small" icon="add" clickable action={add} />]}>
              {
                value[node.id] ? value[node.id].map((field, index) => <TreeItem icon={field.icon || "description"} label={field.name || field.label} onSelect={() => select(index, field, node)} />) : null
              }
            </TreeNode>
          ))}
        </Tree>
      </menu>
      <div>
        {selected ? <TabbedContentEditor content={selected.item} onChange={change} /> : "select"}
      </div>
    </div>
  )
}

/**
 * FieldEditor
 */
export const FieldEditor = ({ field, onChange, content, outlined = false }) => {
  const { id, type, item, label, editable, options, hidden = false } = field

  function change(id, value) {
    if (onChange) onChange(id, value)
  }

  function renderField() {
    const value1 = field.value || field.value === "" ? field.value : field.default
    const isHidden = CHECK['isFunction'](hidden) ? hidden(field, value1) : hidden
    if (isHidden) {
      return null
    } else {
      switch (type) {
        case TYPES.ENTITY:
          return <EntityEditor field={field} value={value1} onChange={change} />
        case TYPES.STRING:
          return <StringEditor outlined={outlined} field={field} value={value1} onChange={change} content={content} />
        case TYPES.BOOLEAN:
          return <CheckBox outlined id={id} label={label} value={value1} onChange={change} />
        case TYPES.NUMBER:
          return <NumberEditor outlined={outlined} field={field} value={value1} onChange={change} />
        case TYPES.ARRAY:
          return item === TYPES.STRING ?
            options ? <MultiSelectionEditor content={content} field={field} value={value1} onChange={change} /> : <ListEditor field={field} value={value1} onChange={change} />
            : <CollectionEditor field={field} value={value1} onChange={change} />
        default:
          return <div>{label}</div>
      }
    }
  }
  return renderField()
}

/**
 * Entity Editor
 */
const EntityEditor = ({ field, value = {}, onChange }) => {
  const { id, item, label } = field
  const content = new Content(item, value)

  function change(fid, value) {
    const next = Object.assign({}, content.value(), { [fid]: value })
    if (onChange) onChange(id, next)
  }

  const form = content.form()
  const fields = Object.keys(form).map((key) => form[key])

  return (
    <div className='entity-editor'>
      <header>
        <Text use='caption'>{label}</Text>
      </header>
      <main>
        {fields.map((field) => (
          <FieldEditor key={field.id} field={field} onChange={change} />
        ))}
      </main>
    </div>
  )
}

/**
 * String Editor
 */
export const StringEditor = ({ field, value = '', onChange, content, outlined }) => {
  const { id, format, label, options, editable = true, predictive = false, multivalue = false, Editor } = field

  function change(id, value) {
    if (onChange) onChange(id, value)
  }

  function buildOptions() {
    const opts = typeof options === 'function' ? options(field, content) : options
    return opts
  }

  function renderFormat(format, options) {
    switch (format) {
      case FORMATS.COLOR: return <ColorField id={id} onChange={change} value={value}/>
      case FORMATS.HTML: return <Editor id={id} value={value} onChange={change} content={content} />
      case FORMATS.DATE: return <TextField outlined={outlined} id={id} type="date" label={label} value={value} onChange={change} readOnly={!editable} />
      case FORMATS.TOKENS: return <TokenField id={id} label={label} onChange={change} readOnly={!editable} options={options} />
      default:
        return options ? (
          <DropDown outlined={outlined} id={id} label={label} value={value} onChange={change} options={buildOptions()} readOnly={!editable} predictive={predictive} />
        ) : multivalue ? (
          <TokenField id={id} label={label} onChange={change} readOnly={!editable}/>
        ) : (
          <TextField outlined={outlined} id={id} label={label} value={value} onChange={change} readOnly={!editable} />
        )
    }
  }

  return (
    <div className={`field-editor string-editor ${id}`}>
      {renderFormat(format, options)}
    </div>
  )
}

/**
 * Number Editor
 */
const NumberEditor = ({ field, value, onChange, outlined = false }) => {
  const { id, label, editable = true } = field

  function change(id, value) {
    if (onChange) onChange(id, value)
  }

  const val = value || value === "" ? value : field.default
  const min = field.min
  const max = field.max
  const disabled = !editable

  console.log('NumberEditor: ', label, val, value)

  return (
    <div className='field-editor number-editor'>
      <TextField outlined={outlined} id={id} label={label} type='NUMBER' value={val} max={max} min={min} onChange={change} disabled={disabled} />
    </div>
  )
}

/**
 * List Editor
 */
export const ListEditor = ({ field, value = [], onChange }) => {

  const { label } = field

  function change(id, value) {
    if (onChange) onChange(id, value)
  }

  return (
    <div className="list-editor">
      <TokenField id={field.id} label={label} init={value} onChange={change} />
    </div>
  )
}

/**
 * Multi Selection Editor
 */
export const MultiSelectionEditor = ({ field, value = [], content, onChange }) => {

  const { id, label, options } = field

  function change(v) {
    const index = value.indexOf(v)
    if (index >= 0) {
      value.splice(index, 1)
    } else {
      value.push(v)
    }
    if (onChange) onChange(id, value)
  }

  function buildOptions() {
    const opts = typeof options === 'function' ? options(field, content) : options
    return opts
  }

  return (
    <div className="multiselection-editor">
      <label>{label}</label>
      {buildOptions().map(option => {
        const checked = value.includes(option.value)
        return <CheckBox value={checked} label={option.label} onChange={() => change(option.value)} />
      })}
    </div>
  )

}

/**
 * Collection Editor
 */
export const CollectionEditor = ({ field, value = [], onChange }) => {

  const { id, item, label, Feeder, Renderer, Adder = true, editable = true } = field

  function add(rows) {
    if (onChange) {
      const next = value.concat(rows)
      onChange(id, next)
    }
  }

  function remove(index) {
    if (onChange) {
      const next = value.filter((item, i) => i !== index)
      onChange(id, next)
    }
  }

  function change(index, cellId, cellValue) {
    if (onChange) {
      const next = value.slice()
      next[index][cellId] = cellValue
      onChange(id, next)
    }
  }

  const columns = Object.values(item).map((item) => ({ ...item, onChange: change }))
  columns.push({ id: 'actions', label: 'Actions' })

  const rows = value.map((item, index) => ({
    ...item,
    id: index,
    actions: [
      <Icon icon='delete' clickable action={() => remove(index)} size="small" />
    ]
  }))

  const table = { columns, rows }

  return (
    <div className='collection-editor'>
      <header>
        <Text use='caption'>{label}</Text>
        <div className="actions" >
          {Feeder ? <Feeder onAdd={add} /> : null}
        </div>
      </header>
      {Renderer ? <Renderer field={field} value={value} onRemove={remove} onChange={onChange} /> : <DataTable {...table} editable={editable} />}
      <footer>
        {Adder ? <CollectionAdder item={item} onAdd={add} /> : null}
      </footer>
    </div>
  )
}

/**
 * Collection Adder
 */
const CollectionAdder = ({ item, onAdd }) => {

  const [edit, setEdit] = useState(false)
  const [form, setForm] = useState({})

  function toggle() {
    setEdit(!edit)
  }

  function change(index, cellId, cellValue) {
    if (onAdd) {
      const next = Object.assign({}, form, { [cellId]: cellValue })
      setForm(next)
    }
  }

  function save() {
    if (onAdd) onAdd([form])
    toggle()
  }

  const columns = Object.values(item).map((item) => ({ ...item, type: 'String', onChange: change }))
  columns.push({ id: 'actions', label: '' })
  const rows = [{
    actions: [<Icon icon="done" clickable action={save} />, <Icon icon="close" clickable action={toggle} />]
  }]
  rows[0] = Object.assign({}, form, rows[0])
  const table = { columns, rows }

  return (
    <div className="collection-adder">
      {edit ? <DataTable {...table} editable={true} /> : <Button icon='add' label='Añadir' action={toggle} />}
    </div>
  )
}